import { getRuntimeApiBase, isCapacitorLocalHost, isNativeAppRuntime } from '@/config/runtime'

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1'])

function getConfiguredApiBase(): string {
  return getRuntimeApiBase()
}

function getAppOrigin(): string | null {
  if (typeof window === 'undefined') return null
  return window.location.origin
}

/** Origem da API remota (VITE_API_URL). */
function getApiOrigin(): string | null {
  const base = getConfiguredApiBase()
  const appOrigin = getAppOrigin()

  if (!base) return appOrigin

  try {
    return new URL(base, appOrigin ?? 'https://localhost').origin
  } catch {
    return appOrigin
  }
}

/** App Capacitor (https://localhost) com API noutro domínio — media vem do servidor remoto. */
function isNativeAppShell(): boolean {
  if (typeof window === 'undefined') return false
  if (!getConfiguredApiBase()) return false
  if (isNativeAppRuntime() && isCapacitorLocalHost()) return true
  return isCapacitorLocalHost()
}

/** Origem para resolver /uploads e paths relativos de media. */
function getPreferredMediaOrigin(): string | null {
  const apiOrigin = getApiOrigin()
  const appOrigin = getAppOrigin()

  if (getConfiguredApiBase() && apiOrigin && appOrigin && apiOrigin !== appOrigin) {
    return apiOrigin
  }
  if (isNativeAppShell() && apiOrigin) return apiOrigin

  return appOrigin ?? apiOrigin
}

/**
 * Em produção, o proxy (Apache/nginx) costuma encaminhar só `/api` ao Node.
 * Nesse caso o browser deve pedir `/api/uploads/...`, não `/uploads/...`.
 */
export function shouldServeUploadsViaApi(): boolean {
  if (import.meta.env.VITE_UPLOADS_VIA_API === 'true') return true
  if (import.meta.env.VITE_UPLOADS_VIA_API === 'false') return false
  if (isNativeAppShell()) return true
  if (!import.meta.env.PROD) return false

  const apiUrl = import.meta.env.VITE_API_URL?.trim()
  if (!apiUrl) return true

  const appOrigin = getAppOrigin()
  if (!appOrigin) return true

  try {
    const apiOrigin = new URL(apiUrl, appOrigin).origin
    // API no mesmo host que o site → mesmo proxy; prefixo /api continua necessário
    if (apiOrigin === appOrigin) return true
  } catch {
    return true
  }

  return false
}

/**
 * Mantemos /uploads/... na BD; no browser (produção) pedimos /api/uploads/...
 */
export function uploadPathForBrowser(storedPath: string): string {
  const p = String(storedPath || '').trim()
  if (p.startsWith('/api/uploads/')) return p
  if (!p.startsWith('/uploads/')) return p
  if (shouldServeUploadsViaApi()) return `/api${p}`
  return p
}

function normalizeStoredPath(value: string): string {
  const v = value.trim()
  if (v.startsWith('/api/uploads/')) return v.replace(/^\/api\/uploads\//, '/uploads/')
  if (v.startsWith('/uploads/')) return v
  try {
    const parsed = new URL(v)
    let path = parsed.pathname
    if (path.startsWith('/api/uploads/')) path = path.replace(/^\/api\/uploads\//, '/uploads/')
    if (path.startsWith('/uploads/')) return `${path}${parsed.search}${parsed.hash}`
  } catch {
    /* ignore */
  }
  return v
}

export function resolveMediaUrl(rawUrl: string | null | undefined): string {
  const value = String(rawUrl ?? '').trim()
  if (!value) return ''

  if (value.startsWith('data:') || value.startsWith('blob:')) return value

  const mediaOrigin = getPreferredMediaOrigin()

  if (value.startsWith('//')) {
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:'
    return `${protocol}${value}`
  }

  const stored = normalizeStoredPath(value)

  // Uploads: via /api/uploads (proxy Apache) ou /uploads (Node directo)
  if (stored.startsWith('/uploads/')) {
    const browserPath = uploadPathForBrowser(stored)
    if (mediaOrigin) return new URL(browserPath, mediaOrigin).toString()
    return browserPath
  }

  if (stored.startsWith('/')) {
    if (!mediaOrigin) return stored
    return new URL(stored, mediaOrigin).toString()
  }

  try {
    const parsed = new URL(value)
    const rel = normalizeStoredPath(parsed.pathname)
    if (rel.startsWith('/uploads/') && mediaOrigin) {
      const browserPath = uploadPathForBrowser(rel)
      return new URL(`${browserPath}${parsed.search}${parsed.hash}`, mediaOrigin).toString()
    }
    if (LOCAL_HOSTS.has(parsed.hostname) && mediaOrigin) {
      return new URL(`${parsed.pathname}${parsed.search}${parsed.hash}`, mediaOrigin).toString()
    }
    return parsed.toString()
  } catch {
    if (!mediaOrigin) return value
    const normalizedPath = value.startsWith('./') ? value.slice(1) : `/${value.replace(/^\/+/, '')}`
    const inner = normalizeStoredPath(normalizedPath)
    if (inner.startsWith('/uploads/')) {
      return new URL(uploadPathForBrowser(inner), mediaOrigin).toString()
    }
    return new URL(normalizedPath, mediaOrigin).toString()
  }
}

/**
 * Devolve a forma "guardável" do URL de uma media:
 *  - caminhos servidos pelo backend (/uploads/...) são guardados como caminho relativo,
 *    para que continuem a funcionar mesmo que o domínio da API mude;
 *  - URLs externas (ex.: imgur.com) são mantidas tal e qual;
 *  - data:/blob:/inputs vazios devolvem string vazia (nada a guardar).
 *
 * Use sempre antes de persistir uma URL de imagem/vídeo na BD.
 */
export function toStorableMediaUrl(rawUrl: string | null | undefined): string {
  const value = String(rawUrl ?? '').trim()
  if (!value) return ''
  if (value.startsWith('data:') || value.startsWith('blob:')) return ''

  const normalized = normalizeStoredPath(value)
  if (normalized.startsWith('/uploads/')) return normalized

  if (value.startsWith('/')) return value.replace(/^\/+/, '/')

  try {
    const parsed = new URL(value)
    const apiOrigin = getApiOrigin()
    const isLocalHost = LOCAL_HOSTS.has(parsed.hostname)
    const isApiHost = apiOrigin ? new URL(apiOrigin).hostname === parsed.hostname : false
    const isAppHost =
      typeof window !== 'undefined' ? window.location.hostname === parsed.hostname : false

    if (parsed.pathname.startsWith('/uploads/') && (isLocalHost || isApiHost || isAppHost)) {
      return normalizeStoredPath(`${parsed.pathname}${parsed.search}${parsed.hash}`)
    }
    if (parsed.pathname.startsWith('/api/uploads/') && (isLocalHost || isApiHost || isAppHost)) {
      return normalizeStoredPath(parsed.pathname)
    }
    return parsed.toString()
  } catch {
    return value
  }
}

